import type { BufferAttribute, BufferGeometry, InterleavedBufferAttribute, Texture } from "three";
import { isGeometry, isInterleavedBufferAttribute, isTexture } from "./helper.js";
import { estimateTextureMemory } from "./helper.textures.js";

// #region cross-resource memory accounting
//
// Per-resource estimators (estimateGeometryMemory / estimateTextureMemory) sum a
// single resource. Aggregating them naively DOUBLE-COUNTS storage shared across
// DIFFERENT resources (two geometries sharing an attribute array, two textures
// sharing image data). Dedupe against a shared accumulator instead.
//
// CPU and VRAM dedupe on DIFFERENT keys:
//   - CPU (JS heap): one allocation per backing `ArrayBuffer` (or image object).
//     Two typed-array views over one ArrayBuffer cost one allocation.
//   - VRAM (GPU upload): three keys its GPU buffers/textures in WeakMaps on the
//     `BufferAttribute` / `InterleavedBuffer` / `Texture` OBJECT. Two geometries
//     sharing the same attribute object = one upload; sharing only the ArrayBuffer
//     via separate attributes = TWO uploads. So dedupe VRAM by the three object.

export interface MemoryAcc {
    /** CPU dedupe: backing `ArrayBuffer`s / image objects already counted. */
    readonly cpuKeys: Set<unknown>;
    /** VRAM dedupe keys already uploaded — geometry attribute objects, and for
     *  textures a `source.uuid|samplerKey` string (three caches GPU textures per
     *  Source + sampler params, refcounted, NOT per Texture object). */
    readonly gpuKeys: Set<unknown>;
    cpu: number;
    vram: number;
}

/**
 * three.js caches the GPU texture per `Source` keyed by sampler parameters (see
 * `WebGLTextures.getTextureCacheKey`) — wrap / filter / format / flipY /
 * colorSpace, NOT the image. Textures sharing a `source` AND these params share
 * ONE GPU allocation (refcounted via `usedTimes`). This mirrors that key so we
 * don't count N uploads for N Texture objects over one shared source.
 */
function textureVramKey(texture: Texture): unknown {
    const t = texture as unknown as Record<string, unknown> & { source?: { uuid?: string } };
    const uuid = t.source?.uuid;
    if (!uuid) return texture; // pre-Source three: fall back to the object
    const sampler = [
        t.wrapS, t.wrapT, t.wrapR ?? 0, t.magFilter, t.minFilter, t.anisotropy,
        t.internalFormat, t.format, t.type, t.generateMipmaps, t.premultiplyAlpha,
        t.flipY, t.unpackAlignment, t.colorSpace,
    ].join(",");
    return `${uuid}|${sampler}`;
}

export function createMemoryAcc(): MemoryAcc {
    return { cpuKeys: new Set(), gpuKeys: new Set(), cpu: 0, vram: 0 };
}

type AnyAttribute = BufferAttribute | InterleavedBufferAttribute;

/** Fold a geometry's attribute/index/morph buffers into the accumulator. */
export function accumulateGeometryMemory(geometry: BufferGeometry, acc: MemoryAcc): void {
    const add = (attribute: AnyAttribute | null | undefined): void => {
        if (!attribute) return;
        const interleaved = isInterleavedBufferAttribute(attribute);
        const array = (interleaved
            ? (attribute as InterleavedBufferAttribute).data?.array
            : (attribute as BufferAttribute).array) as ArrayBufferView | undefined;
        if (!array || typeof array.byteLength !== "number") return;
        // CPU: the whole backing ArrayBuffer, once (shared by every view over it).
        const buffer = array.buffer;
        if (buffer && !acc.cpuKeys.has(buffer)) { acc.cpuKeys.add(buffer); acc.cpu += buffer.byteLength; }
        // VRAM: one GPU buffer per BufferAttribute / InterleavedBuffer object.
        const gpuKey: object = interleaved ? (attribute as InterleavedBufferAttribute).data : attribute;
        if (gpuKey && !acc.gpuKeys.has(gpuKey)) { acc.gpuKeys.add(gpuKey); acc.vram += array.byteLength; }
    };
    for (const name in geometry.attributes) add(geometry.attributes[name] as AnyAttribute);
    add(geometry.index as BufferAttribute | null);
    const morphAttributes = geometry.morphAttributes as Record<string, AnyAttribute[]> | undefined;
    if (morphAttributes) {
        for (const name in morphAttributes) {
            const morphs = morphAttributes[name];
            if (Array.isArray(morphs)) for (const m of morphs) add(m);
        }
    }
}

/** Fold a texture's CPU/VRAM into the accumulator, deduped by shared storage. */
export function accumulateTextureMemory(texture: Texture, acc: MemoryAcc): void {
    const { cpu, vram } = estimateTextureMemory(texture);
    // VRAM: one GPU texture per Source + sampler-params (three refcounts it) —
    // NOT per Texture object. N textures over one shared source = one upload.
    const vramKey = textureVramKey(texture);
    if (vram > 0 && !acc.gpuKeys.has(vramKey)) { acc.gpuKeys.add(vramKey); acc.vram += vram; }
    // CPU: dedupe on a shareable backing — the data ArrayBuffer, else the image
    // object (two textures pointing at the same ImageBitmap share the pixels),
    // else the texture itself (nothing shareable → counts as unique).
    if (cpu > 0) {
        const image = (texture as { image?: { data?: ArrayBufferView } }).image;
        const cpuKey: object = image?.data?.buffer ?? image ?? texture;
        if (!acc.cpuKeys.has(cpuKey)) { acc.cpuKeys.add(cpuKey); acc.cpu += cpu; }
    }
}

/**
 * Deduped memory total across many resources — backing storage shared between
 * resources (one ArrayBuffer used by two geometries, one image by two textures)
 * counts ONCE. Non-geometry/texture resources are ignored.
 */
export function estimateResourcesMemory(resources: Iterable<object>): { cpu: number; vram: number } {
    const acc = createMemoryAcc();
    for (const r of resources) {
        if (!r) continue;
        if (isTexture(r)) accumulateTextureMemory(r, acc);
        else if (isGeometry(r)) accumulateGeometryMemory(r, acc);
    }
    return { cpu: acc.cpu, vram: acc.vram };
}

// #endregion

// #region redundant / duplicated data
//
// Sharing (above) is ONE allocation referenced twice — counting it once is
// correct. DUPLICATION is different: two SEPARATE allocations that happen to
// hold byte-identical content (the same texture loaded 4×, two copies of one
// mesh). That memory genuinely exists N times — real memory must still count it
// N× (it IS resident N×). But it's reclaimable: dedupe the asset and save
// (N-1)× the bytes. We surface it as a separate "redundant" signal, never by
// folding it into the memory total (that would UNDER-report actual RAM/VRAM).
//
// Detection is by CONTENT FINGERPRINT: a fast, cached FNV-1a hash of the backing
// bytes (data textures, compressed mipmaps, geometry buffers), or the source URL
// for image-element textures. Textures whose pixels live only on the GPU (an
// ImageBitmap with no readable bytes and no URL) can't be proven identical
// without a readback, so they're left out of the redundancy count — honest
// under-claim rather than a false positive.

const _hashCache = new WeakMap<object, number>();

/** FNV-1a over a u32 view; strides very large buffers (cached redundancy HINT, not a checksum). */
function fnv1aU32(u32: Uint32Array, seed: number): number {
    let h = (0x811c9dc5 ^ seed) >>> 0;
    const step = u32.length > 1 << 18 ? Math.max(1, (u32.length / (1 << 18)) | 0) : 1;
    for (let i = 0; i < u32.length; i += step) { h ^= u32[i]; h = Math.imul(h, 0x01000193); }
    return h >>> 0;
}

/** Content hash of a typed-array VIEW (the uploaded range), cached per view object. */
function hashView(view: ArrayBufferView): number {
    const cached = _hashCache.get(view);
    if (cached !== undefined) return cached;
    const h = fnv1aU32(new Uint32Array(view.buffer, view.byteOffset, view.byteLength >>> 2), view.byteLength);
    _hashCache.set(view, h);
    return h;
}

/** Byte-content fingerprint of a texture, or null when the bytes can't be reached cheaply. */
function textureContentKey(texture: Texture): string | null {
    const image = (texture as { image?: { data?: ArrayBufferView; src?: string; currentSrc?: string } }).image;
    const fmt = `${(texture as { format?: unknown }).format}:${(texture as { type?: unknown }).type}`;
    const data = image?.data;
    if (data && typeof data.byteLength === "number" && data.buffer) {
        return `T:${data.byteLength}:${fmt}:${hashView(data).toString(16)}`;
    }
    const mipmaps = (texture as { mipmaps?: Array<{ data?: ArrayBufferView }> }).mipmaps;
    if (mipmaps?.length) {
        let h = 0;
        let any = false;
        for (const m of mipmaps) if (m?.data?.buffer) { h = (Math.imul(h, 31) + hashView(m.data)) >>> 0; any = true; }
        if (any) return `TM:${fmt}:${h.toString(16)}`;
    }
    const src = image?.currentSrc || image?.src;
    if (src) return `TS:${fmt}:${src}`;
    return null;
}

/** Byte-content fingerprint of a geometry (all attribute/index buffers), or null when empty. */
function geometryContentKey(geometry: BufferGeometry): string | null {
    const view = (attribute: AnyAttribute | null | undefined): ArrayBufferView | undefined => {
        if (!attribute) return undefined;
        return (isInterleavedBufferAttribute(attribute)
            ? (attribute as InterleavedBufferAttribute).data?.array
            : (attribute as BufferAttribute).array) as ArrayBufferView | undefined;
    };
    const parts: string[] = [];
    for (const name of Object.keys(geometry.attributes).sort()) {
        const a = view(geometry.attributes[name] as AnyAttribute);
        if (a) parts.push(`${name}:${a.byteLength}:${hashView(a).toString(16)}`);
    }
    const index = view(geometry.index as BufferAttribute | null);
    if (index) parts.push(`i:${index.byteLength}:${hashView(index).toString(16)}`);
    return parts.length ? "G|" + parts.join("|") : null;
}

/**
 * Byte-content fingerprint of a resource (texture or geometry) — two resources
 * with the SAME key hold identical data. `null` when the content can't be
 * proven cheaply (GPU-only image with no bytes / URL). Used to fold identical
 * resources together in the UI.
 */
export function resourceContentKey(resource: object): string | null {
    if (isTexture(resource)) return textureContentKey(resource as Texture);
    if (isGeometry(resource)) return geometryContentKey(resource as BufferGeometry);
    return null;
}

/** The CPU and VRAM allocation identities of a resource — sharing one means one real allocation. */
function allocKeysFor(resource: object): { cpu: unknown; vram: unknown } | null {
    if (isTexture(resource)) {
        const image = (resource as { image?: { data?: { buffer?: object } } }).image;
        // CPU: the decoded image (source data) is shared by every Texture over the
        // same Source. VRAM: one GPU texture per Source + sampler params (three
        // refcounts it), so shared-source textures are NOT duplicated uploads.
        return {
            cpu: image?.data?.buffer ?? (image as object | undefined) ?? resource,
            vram: textureVramKey(resource as Texture),
        };
    }
    if (isGeometry(resource)) {
        const pos = (resource as BufferGeometry).attributes?.position as AnyAttribute | undefined;
        const posArray = pos
            ? ((isInterleavedBufferAttribute(pos)
                  ? (pos as InterleavedBufferAttribute).data?.array
                  : (pos as BufferAttribute).array) as ArrayBufferView | undefined)
            : undefined;
        // position stands in for the whole geometry: distinct position buffer ⇒
        // distinct CPU allocation; distinct position attribute ⇒ distinct upload.
        return { cpu: posArray?.buffer ?? resource, vram: (pos as object | undefined) ?? resource };
    }
    return null;
}

// Stable string ids for object allocation keys (backing buffers / attributes) —
// so a caller can dedupe allocations by primitive WITHOUT holding the live
// object across async work (which would extend host-object lifetimes). The
// WeakMap holds keys weakly, so it never retains anything.
const _allocIds = new WeakMap<object, number>();
let _allocSeq = 0;
function allocIdString(key: unknown): string {
    if (typeof key === "string") return key; // already primitive (e.g. texture source|sampler)
    const obj = key as object;
    let id = _allocIds.get(obj);
    if (id === undefined) _allocIds.set(obj, (id = ++_allocSeq));
    return "#" + id;
}

/**
 * The CPU and VRAM allocation identities of a resource as PRIMITIVE strings —
 * two resources with the same string share that allocation (one image buffer,
 * one GPU upload). Lets an async/queued analyzer dedupe allocations without
 * retaining live objects. `null` for non-texture/geometry resources.
 */
export function resourceAllocKeys(resource: object): { cpu: string; vram: string } | null {
    const keys = allocKeysFor(resource);
    if (!keys) return null;
    return { cpu: allocIdString(keys.cpu), vram: allocIdString(keys.vram) };
}

export interface ResourceMemoryReport {
    /** Real resident memory (shared allocations counted once, duplicates counted N×). */
    cpu: number;
    vram: number;
    /**
     * Bytes reclaimable if byte-identical duplicates were collapsed to one
     * allocation. CPU and VRAM are separate: 4 textures over 1 image are SHARED
     * on CPU (redundantCpu 0) but DUPLICATED on GPU (redundantVram = 3 uploads).
     */
    redundantCpu: number;
    redundantVram: number;
    /** Resources whose content is byte-identical to ≥1 OTHER distinct allocation → number of copies (≥2). */
    duplicates: Map<object, number>;
}

/**
 * Real memory (as {@link estimateResourcesMemory}) PLUS duplicate-data analysis:
 * how much of that memory is byte-identical copies that could be deduped, and
 * which resources are duplicated. The total is NOT reduced by duplication —
 * duplicated bytes are really resident N times.
 */
export function analyzeResourcesMemory(resources: Iterable<object>): ResourceMemoryReport {
    const acc = createMemoryAcc();
    const infos: Array<{ resource: object; content: string | null; cpuKey: unknown; vramKey: unknown; cpu: number; vram: number }> = [];

    for (const r of resources) {
        if (!r) continue;
        const keys = allocKeysFor(r);
        if (!keys) continue;
        if (isTexture(r)) {
            const { cpu, vram } = estimateTextureMemory(r);
            accumulateTextureMemory(r, acc);
            infos.push({ resource: r, content: textureContentKey(r), cpuKey: keys.cpu, vramKey: keys.vram, cpu, vram });
        } else if (isGeometry(r)) {
            const own = createMemoryAcc();
            accumulateGeometryMemory(r, own);
            accumulateGeometryMemory(r, acc);
            infos.push({ resource: r, content: geometryContentKey(r), cpuKey: keys.cpu, vramKey: keys.vram, cpu: own.cpu, vram: own.vram });
        }
    }

    // CPU and VRAM duplicate on DIFFERENT allocation keys — group each independently.
    const cpuGroups = new Map<string, Set<unknown>>();
    const vramGroups = new Map<string, Set<unknown>>();
    const repCpu = new Map<string, number>();
    const repVram = new Map<string, number>();
    for (const info of infos) {
        if (info.content == null) continue;
        let c = cpuGroups.get(info.content);
        if (!c) { c = new Set(); cpuGroups.set(info.content, c); repCpu.set(info.content, info.cpu); repVram.set(info.content, info.vram); }
        c.add(info.cpuKey);
        let v = vramGroups.get(info.content);
        if (!v) { v = new Set(); vramGroups.set(info.content, v); }
        v.add(info.vramKey);
    }

    let redundantCpu = 0;
    let redundantVram = 0;
    for (const [content, set] of cpuGroups) redundantCpu += Math.max(0, set.size - 1) * (repCpu.get(content) ?? 0);
    for (const [content, set] of vramGroups) redundantVram += Math.max(0, set.size - 1) * (repVram.get(content) ?? 0);

    // copies = the WORST duplication across the two tiers (4 GPU uploads of a shared image = 4).
    const duplicates = new Map<object, number>();
    for (const info of infos) {
        if (info.content == null) continue;
        const n = Math.max(cpuGroups.get(info.content)!.size, vramGroups.get(info.content)!.size);
        if (n >= 2) duplicates.set(info.resource, n);
    }

    return { cpu: acc.cpu, vram: acc.vram, redundantCpu, redundantVram, duplicates };
}

// #endregion
